// ============================================================
// WEBSITE PREVIEW — LIVE WEBSITE (dibaca pengunjung publik)
// ============================================================
import React, { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import DOMPurify from 'dompurify';
import ReCAPTCHA from 'react-google-recaptcha';

// ── Config ──────────────────────────────────────────────────────────────────
// These constants mirror the ones in App.js.
// If you create a shared config file (e.g. src/config.js), import from there
// instead of duplicating them here.
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
const LOGO_URL = 'https://supa-site.aptana.co.id/storage/v1/object/public/Aptana-Website/home/logo%20aptana.svg';

// ── Article Typography CSS ───────────────────────────────────────────────────
const articleStyles = `
  /* Base text — readable on dark background */
  .article-content { color: rgba(255,255,255,0.82); }
  .article-content h1 { font-size: clamp(22px,4vw,28px); font-weight: 700; color: #ffffff; margin: 32px 0 16px; line-height: 1.3; }
  .article-content h2 { font-size: clamp(20px,3.5vw,24px); font-weight: 700; color: #ffffff; margin: 28px 0 14px; line-height: 1.3; }
  .article-content h3 { font-size: clamp(17px,3vw,20px); font-weight: 600; color: rgba(255,255,255,0.9); margin: 24px 0 12px; line-height: 1.4; }
  .article-content h4 { font-size: 17px; font-weight: 600; color: rgba(255,255,255,0.9); margin: 20px 0 10px; }
  .article-content p  { margin: 18px 0; line-height: 1.9; color: rgba(255,255,255,0.78); }
  .article-content p:first-child { margin-top: 0; }
  .article-content p:last-child  { margin-bottom: 0; }
  .article-content strong, .article-content b { font-weight: 700; color: #ffffff; }
  .article-content em, .article-content i { font-style: italic; color: rgba(255,255,255,0.7); }
  .article-content ul, .article-content ol { margin: 18px 0; padding-left: 28px; }
  .article-content li { margin: 10px 0; line-height: 1.8; color: rgba(255,255,255,0.78); }
  .article-content blockquote { margin: 24px 0; padding: 16px 20px; background: rgba(232,168,0,0.06); border-left: 4px solid #E8A800; font-style: italic; color: rgba(255,255,255,0.7); border-radius: 0 8px 8px 0; }
  .article-content blockquote p { margin: 0; color: inherit; }
  .article-content code { background: rgba(255,255,255,0.08); padding: 2px 7px; border-radius: 4px; font-family: monospace; font-size: 14px; color: #E8A800; }
  .article-content pre  { background: rgba(255,255,255,0.04); padding: 16px; border-radius: 8px; overflow-x: auto; margin: 20px 0; border: 1px solid rgba(255,255,255,0.08); }
  .article-content img  { max-width: 100%; height: auto; border-radius: 8px; margin: 24px auto; display: block; border: 1px solid rgba(255,255,255,0.08); }
  .article-content a    { color: #E8A800; text-decoration: none; font-weight: 600; }
  .article-content a:hover { text-decoration: underline; color: #FFD166; }
  .article-content table { width: 100%; border-collapse: collapse; margin: 24px 0; }
  .article-content th, .article-content td { padding: 12px; text-align: left; border: 1px solid rgba(255,255,255,0.1); color: rgba(255,255,255,0.78); }
  .article-content th { background: rgba(255,255,255,0.05); font-weight: 700; color: #ffffff; }
  .article-content hr  { border: none; height: 1px; background: rgba(255,255,255,0.1); margin: 32px 0; }
  /* Override hardcoded dark colors from old articles */
  .article-content [style*="color: #000000"], .article-content [style*="color:#000000"],
  .article-content [style*="color: #000"], .article-content [style*="color:#000"],
  .article-content [style*="color: #333333"], .article-content [style*="color:#333333"],
  .article-content [style*="color: #333"], .article-content [style*="color:#333"],
  .article-content [style*="color: #2d3648"], .article-content [style*="color:#2d3648"],
  .article-content [style*="color: #595959"], .article-content [style*="color:#595959"],
  .article-content [style*="color: #666"], .article-content [style*="color:#666"],
  .article-content [style*="color: rgb(0,0,0)"], .article-content [style*="color: black"]
    { color: rgba(255,255,255,0.78) !important; }
  .article-content [style*="background-color: #ffffff"], .article-content [style*="background-color:#ffffff"],
  .article-content [style*="background-color: white"], .article-content [style*="background-color: #fff"],
  .article-content [style*="background: white"], .article-content [style*="background: #fff"],
  .article-content [style*="background: #ffffff"]
    { background-color: transparent !important; }
  /* Responsive blog layout */
  .blog-pad { padding: 100px 0; }
  .blog-inner { padding: 0 56px; }
  .blog-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 28px; }
  .article-outer { padding: 0 56px; }
  .detail-card { padding: 52px; }
  .footer-grid { display: grid; grid-template-columns: 2.2fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 52px; }
  .footer-outer { padding: 72px 56px 44px; }
  .nav-desktop { display: flex !important; }
  .nav-hamburger { display: none !important; }
  @media (max-width: 1024px) {
    .blog-grid { grid-template-columns: repeat(2,1fr); }
  }
  @media (max-width: 768px) {
    .blog-inner, .article-outer, .blog-related-inner { padding: 0 20px !important; }
    .blog-grid { grid-template-columns: 1fr; gap: 20px; }
    .blog-pad { padding: 60px 0; }
    .detail-card { padding: 24px !important; }
    .footer-grid { grid-template-columns: 1fr 1fr; gap: 28px; margin-bottom: 36px; }
    .footer-outer { padding: 48px 20px 32px; }
    .nav-desktop { display: none !important; }
    .nav-hamburger { display: flex !important; }
  }
  @media (max-width: 480px) {
    .footer-grid { grid-template-columns: 1fr; }
    .blog-pad { padding: 48px 0; }
  }
`;

// ── Shared Components ────────────────────────────────────────────────────────

const NAV_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/multichannel', label: 'Multichannel' },
  { href: '/kuponku', label: 'Kuponku' },
  { href: '/tentang', label: 'Tentang Kami' },
  { href: '/blog', label: 'Blog' },
  { href: '/karir', label: 'Karir' },
  { href: '/kontak', label: 'Konsultasi Gratis', cta: true },
];

function SiteHeader({ currentPage }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 991 : false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 991);
      if (window.innerWidth > 991) setMobileMenuOpen(false);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const slug = currentPage === 'home' ? 'home' : currentPage;

  return (
    <>
      <nav style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 200,
        height: '64px',
        padding: '0 56px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'background 0.5s ease, border-color 0.5s ease, backdrop-filter 0.5s ease',
        borderBottom: '1px solid transparent',
        background: scrolled ? 'rgba(6,16,30,0.88)' : 'transparent',
        borderBottomColor: scrolled ? 'rgba(255,255,255,0.07)' : 'transparent',
        backdropFilter: scrolled ? 'blur(24px) saturate(1.4)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(24px) saturate(1.4)' : 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img
              src="https://supa-site.aptana.co.id/storage/v1/object/public/Aptana-Website/home/Logo%20Aptana%20(light).svg"
              alt="Aptana"
              style={{ height: '52px', width: 'auto', display: 'block' }}
            />
          </a>
        </div>

        <ul className="nav-desktop" style={{ alignItems: 'center', gap: '30px', listStyle: 'none', margin: 0, padding: 0 }}>
          {NAV_LINKS.map(({ href, label, cta }) => {
            const isActive = currentPage === (href === '/' ? 'home' : href.replace('/', ''));
            return (
              <li key={href}>
                <a
                  href={href}
                  style={cta ? {
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#fff',
                    textDecoration: 'none',
                    background: 'rgba(255,255,255,0.1)',
                    padding: '8px 20px',
                    borderRadius: '980px',
                    border: '1px solid rgba(255,255,255,0.18)',
                    transition: 'background 0.2s, border-color 0.2s',
                    display: 'inline-block',
                    cursor: 'pointer',
                  } : {
                    fontSize: '13px',
                    fontWeight: 500,
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                    letterSpacing: '-0.1px',
                    textDecoration: 'none',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (cta) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.18)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)';
                    } else {
                      e.currentTarget.style.color = '#fff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (cta) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
                    } else {
                      e.currentTarget.style.color = isActive ? '#fff' : 'rgba(255,255,255,0.55)';
                    }
                  }}
                >
                  {label}
                </a>
              </li>
            );
          })}
        </ul>

        <div
            className="nav-hamburger"
            style={{
              flexDirection: 'column',
              gap: '5px',
              cursor: 'pointer',
              padding: '4px'
            }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span style={{ display: 'block', width: '22px', height: '1.5px', background: 'rgba(255,255,255,0.75)', borderRadius: '2px' }} />
            <span style={{ display: 'block', width: '22px', height: '1.5px', background: 'rgba(255,255,255,0.75)', borderRadius: '2px' }} />
            <span style={{ display: 'block', width: '22px', height: '1.5px', background: 'rgba(255,255,255,0.75)', borderRadius: '2px' }} />
          </div>
      </nav>

      {mobileMenuOpen && (
        <div style={{
          display: 'flex',
          position: 'fixed',
          top: '64px', left: 0, right: 0,
          background: 'rgba(6,16,30,0.97)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          zIndex: 199,
          padding: '20px 28px 28px',
          flexDirection: 'column',
          backdropFilter: 'blur(24px)',
        }}>
          {NAV_LINKS.map(({ href, label, cta }) => {
            const isActive = currentPage === (href === '/' ? 'home' : href.replace('/', ''));
            return (
              <a
                key={href}
                href={href}
                onClick={() => setMobileMenuOpen(false)}
                style={cta ? {
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#06101E',
                  background: '#E8A800',
                  padding: '13px 20px',
                  marginTop: '8px',
                  borderRadius: '980px',
                  textDecoration: 'none',
                  textAlign: 'center',
                } : {
                  fontSize: '15px',
                  fontWeight: 500,
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                  padding: '14px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.07)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                }}
              >
                {label}
              </a>
            );
          })}
        </div>
      )}
    </>
  );
}

function SiteFooter() {
  return (
    <footer className="footer-outer" style={{
      background: '#040A14',
      borderTop: '1px solid rgba(255,255,255,0.07)',
    }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div className="footer-grid">
        <div>
          <div style={{ marginBottom: '18px' }}>
            <img
              src="https://supa-site.aptana.co.id/storage/v1/object/public/Aptana-Website/home/Logo%20Aptana%20(light).svg"
              alt="Aptana"
              style={{ height: '52px', width: 'auto', marginBottom: '18px' }}
            />
          </div>
          <p style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.28)', lineHeight: 1.75, maxWidth: '240px', marginBottom: '20px', letterSpacing: '-0.1px' }}>
            PT Aptana Citra Solusindo — Marketing Solution Partner sejak 2015. Membantu 500+ bisnis tumbuh di era digital.
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10px', fontWeight: '600', padding: '3px 12px', borderRadius: '980px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.3px' }}>
              Meta BSP
            </span>
            <span style={{ fontSize: '10px', fontWeight: '600', padding: '3px 12px', borderRadius: '980px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.3px' }}>
              ISO Certified
            </span>
            <span style={{ fontSize: '10px', fontWeight: '600', padding: '3px 12px', borderRadius: '980px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.3px' }}>
              Est. 2015
            </span>
          </div>
        </div>

        <div>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: '18px' }}>
            Produk
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <a href="/multichannel" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>Multichannel</a>
            <a href="/kuponku" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>Kuponku</a>
            <a href="/multichannel#whatsapp" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>WhatsApp API</a>
            <a href="/multichannel#rcs" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>RCS</a>
          </div>
        </div>

        <div>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: '18px' }}>
            Perusahaan
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <a href="/tentang" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>Tentang Kami</a>
            <a href="/blog" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>Blog</a>
            <a href="/karir" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>Karir</a>
            <a href="/kontak" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>Kontak</a>
          </div>
        </div>

        <div>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: '18px' }}>
            Kontak
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <a href="mailto:hello@aptana.id" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>hello@aptana.id</a>
            <a href="tel:+628113549900" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>+62 811 354 9900</a>
            <a href="https://www.instagram.com/aptanaofficial/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>Instagram</a>
            <a href="https://www.facebook.com/aptanacitrasolusindo/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>Facebook</a>
            <a href="https://www.youtube.com/@AptanaOfficial" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>YouTube</a>
            <a href="https://id.linkedin.com/company/aptana-citra-solusindo" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', fontWeight: '300', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', letterSpacing: '-0.1px', transition: 'color 0.2s' }}>LinkedIn</a>
          </div>
        </div>
      </div>

      </div>
      <div style={{ paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', fontWeight: '300', color: 'rgba(255,255,255,0.18)' }}>
          &copy; 2015–{new Date().getFullYear()} PT Aptana Citra Solusindo ·{' '}
          <a href="https://kurniawan.co.id/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>Kurniawan Group</a>
          {' '}· All rights reserved
        </span>
        <div style={{ display: 'flex', gap: '20px' }}>
          <a href="/privacy-policy" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.18)', textDecoration: 'none', transition: 'color 0.2s' }}>Privacy Policy</a>
          <a href="/terms" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.18)', textDecoration: 'none', transition: 'color 0.2s' }}>Terms</a>
        </div>
      </div>
    </footer>
  );
}

function SubmissionFormPage() {
  const [captchaToken, setCaptchaToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState('');
  const recaptchaRef = useRef(null);
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeMuaAsAAAAAKvzCJPk3Gde3PTbY7La9kWKCNB7';

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!captchaToken) return setSubmitFeedback('Selesaikan reCAPTCHA sebelum mengirim.');

    const form = event.currentTarget;
    const values = new FormData(form);
    const payload = {
      kota: values.get('kota'),
      email: values.get('email'),
      nomor_hp: values.get('nomor_hp'),
      nama_kontak: values.get('nama_kontak'),
      bidang_bisnis: values.get('bidang_bisnis'),
      nama_perusahaan: values.get('nama_perusahaan'),
      kebutuhan_bisnis: values.get('kebutuhan_bisnis'),
      bersedia_dihubungi: values.get('bersedia_dihubungi') === 'Ya',
      recaptcha_token: captchaToken,
    };

    setSubmitting(true);
    setSubmitFeedback('');
    try {
      const response = await fetch('/api/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Pengiriman gagal.');

      setSubmitFeedback(result.stored
        ? 'Terima kasih. Permintaan Anda berhasil dikirim dan akan segera ditindaklanjuti oleh tim Aptana.'
        : 'Permintaan Anda telah diterima dan sedang kami proses.');
      form.reset();
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } catch {
      setSubmitFeedback('Permintaan belum berhasil dikirim. Silakan coba kembali beberapa saat lagi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="submission-page">
      <style>{articleStyles}</style>
      <style>{`
        .submission-page { min-height: 100vh; background: #06101e; color: #f8fafc; }
        .submission-main { max-width: 1000px; margin: 0 auto; padding: 132px 28px 96px; }
        .submission-intro { max-width: 680px; margin: 0 auto 36px; text-align: center; }
        .submission-eyebrow { color: #e8a800; font-size: 11px; font-weight: 700; letter-spacing: 1.4px; text-transform: uppercase; }
        .submission-title { margin: 14px 0; color: #fff; font-size: 36px; line-height: 1.2; font-weight: 700; }
        .submission-copy { color: rgba(255,255,255,.68); font-size: 15px; line-height: 1.75; }
        .submission-form { padding: 32px; border: 1px solid rgba(255,255,255,.12); border-radius: 12px; background: rgba(255,255,255,.045); }
        .submission-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 20px 18px; }
        .submission-field { min-width: 0; }
        .submission-field-wide { grid-column: 1 / -1; }
        .submission-label { display: block; margin-bottom: 8px; color: rgba(255,255,255,.82); font-size: 12px; font-weight: 600; }
        .submission-input { display: block; width: 100%; min-height: 48px; padding: 12px 14px; border: 1px solid rgba(255,255,255,.16); border-radius: 7px; outline: none; background: rgba(2,8,18,.48); color: #fff; font: inherit; font-size: 14px; }
        .submission-input:focus { border-color: #e8a800; box-shadow: 0 0 0 3px rgba(232,168,0,.14); }
        .submission-input::placeholder { color: rgba(255,255,255,.34); }
        .submission-textarea { min-height: 128px; resize: vertical; }
        .submission-consent { display: flex; align-items: flex-start; gap: 10px; margin: 22px 0; color: rgba(255,255,255,.74); font-size: 13px; line-height: 1.6; }
        .submission-consent input { width: 17px; height: 17px; flex: 0 0 17px; margin-top: 1px; accent-color: #e8a800; }
        .submission-captcha { min-height: 78px; overflow: hidden; }
        .submission-submit { min-height: 48px; margin-top: 8px; padding: 0 24px; border: 0; border-radius: 7px; background: #e8a800; color: #06101e; font: inherit; font-size: 14px; font-weight: 700; }
        .submission-submit:disabled { cursor: not-allowed; opacity: .5; }
        .submission-preview-note { margin: 14px 0 0; color: rgba(255,255,255,.42); font-size: 12px; }
        .submission-page .nav-desktop { display: flex !important; }
        .submission-page .nav-hamburger { display: none !important; }
        .submission-page .footer-outer p { color: rgba(255,255,255,.58) !important; max-width: 280px !important; }
        .submission-page .footer-grid > div > div:first-child:not(:has(img)) { color: rgba(255,255,255,.48) !important; }
        .submission-page .footer-grid a { color: rgba(255,255,255,.68) !important; }
        .submission-page .footer-grid a:hover { color: #fff !important; }
        @media (max-width: 640px) {
          .submission-main { padding: 108px 18px 64px; }
          .submission-title { font-size: 29px; }
          .submission-form { padding: 22px 18px; }
          .submission-grid { grid-template-columns: 1fr; gap: 16px; }
          .submission-field-wide { grid-column: auto; }
        }
        @media (max-width: 991px) {
          .submission-page .nav-desktop { display: none !important; }
          .submission-page .nav-hamburger { display: flex !important; }
        }
      `}</style>
      <SiteHeader currentPage="submission-form" />
      <main className="submission-main">
        <header className="submission-intro">
          <div className="submission-eyebrow">Konsultasi Bisnis Aptana</div>
          <h1 className="submission-title">Ceritakan kebutuhan bisnis Anda</h1>
          <p className="submission-copy">Lengkapi formulir ini. Tim Aptana akan menghubungi Anda untuk membahas solusi yang sesuai.</p>
        </header>
        <form className="submission-form" onSubmit={handleSubmit}>
          <div className="submission-grid">
            <div className="submission-field">
              <label className="submission-label" htmlFor="lead-city">Kota</label>
              <input className="submission-input" id="lead-city" name="kota" autoComplete="address-level2" placeholder="Kota domisili/perusahaan" required />
            </div>
            <div className="submission-field">
              <label className="submission-label" htmlFor="lead-email">Email</label>
              <input className="submission-input" id="lead-email" name="email" type="email" autoComplete="email" placeholder="nama@perusahaan.com" required />
            </div>
            <div className="submission-field">
              <label className="submission-label" htmlFor="lead-phone">Nomor HP</label>
              <input className="submission-input" id="lead-phone" name="nomor_hp" type="tel" autoComplete="tel" placeholder="Contoh: 0812 3456 7890" required />
            </div>
            <div className="submission-field">
              <label className="submission-label" htmlFor="lead-contact">Nama Kontak</label>
              <input className="submission-input" id="lead-contact" name="nama_kontak" autoComplete="name" placeholder="Nama yang bisa kami hubungi" required />
            </div>
            <div className="submission-field">
              <label className="submission-label" htmlFor="lead-industry">Bidang Bisnis</label>
              <input className="submission-input" id="lead-industry" name="bidang_bisnis" placeholder="Contoh: retail, F&B, layanan" required />
            </div>
            <div className="submission-field">
              <label className="submission-label" htmlFor="lead-company">Nama Perusahaan</label>
              <input className="submission-input" id="lead-company" name="nama_perusahaan" autoComplete="organization" placeholder="Nama perusahaan Anda" required />
            </div>
            <div className="submission-field submission-field-wide">
              <label className="submission-label" htmlFor="lead-needs">Kebutuhan Bisnis</label>
              <textarea className="submission-input submission-textarea" id="lead-needs" name="kebutuhan_bisnis" placeholder="Ceritakan tantangan atau solusi yang sedang dicari" required />
            </div>
          </div>
          <label className="submission-consent">
            <input type="checkbox" name="bersedia_dihubungi" value="Ya" required />
            <span>Saya bersedia untuk dihubungi oleh Aptana.</span>
          </label>
          <div className="submission-captcha">
            <ReCAPTCHA ref={recaptchaRef} sitekey={siteKey} onChange={setCaptchaToken} onExpired={() => setCaptchaToken(null)} />
          </div>
          <button className="submission-submit" type="submit" disabled={!captchaToken || submitting}>
            {submitting ? 'Mengirim...' : 'Kirim Permintaan'}
          </button>
          <p className="submission-preview-note" aria-live="polite">
            {submitFeedback || 'Informasi Anda akan digunakan untuk menindaklanjuti kebutuhan bisnis yang disampaikan.'}
          </p>
        </form>
      </main>
      <SiteFooter />
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', background: '#ffffff',
    }}>
      <img src={LOGO_URL} alt="Aptana" style={{ height: 80, objectFit: 'contain', marginBottom: 28 }} />
      <div style={{
        width: 44, height: 44,
        border: '3px solid rgba(30,58,138,0.1)',
        borderTop: '3px solid #1e3a8a',
        borderRadius: '50%',
        animation: 'spin 0.9s linear infinite',
      }} />
      <div style={{ marginTop: 18, color: '#64748b', fontSize: 13, fontWeight: 500 }}>Memuat website...</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function NotFoundPage({ slug }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SiteHeader currentPage={slug} />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: 40 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🚧</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Halaman Tidak Ditemukan</div>
        <div style={{ fontSize: 15, color: '#64748b', marginBottom: 24 }}>Halaman "{slug}" sedang dalam persiapan.</div>
        <a href="/" style={{ padding: '12px 28px', background: '#1e3a8a', color: 'white', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>← Kembali ke Home</a>
      </div>
      <SiteFooter />
    </div>
  );
}

// ── Blog Listing ─────────────────────────────────────────────────────────────
function BlogListingPage({ articles }) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ backgroundColor: '#06101E', color: '#FFFFFF', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
      <style>{articleStyles}</style>
      <SiteHeader currentPage="blog" />

      {/* Hero Section - Dark Theme */}
      <section style={{
        position: 'relative',
        minHeight: '60svh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 'clamp(100px,14vw,140px) clamp(20px,5vw,56px) clamp(64px,10vw,100px)',
        overflow: 'hidden',
        background: '#06101E'
      }}>
        {/* Hero Orbs */}
        <div style={{
          position: 'absolute',
          borderRadius: '50%',
          filter: 'blur(80px)',
          pointerEvents: 'none',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(232,168,0,0.06) 0%, transparent 70%)',
          top: '-20%',
          left: '50%',
          transform: 'translateX(-50%)'
        }} />
        <div style={{
          position: 'absolute',
          borderRadius: '50%',
          filter: 'blur(80px)',
          pointerEvents: 'none',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(59,130,246,0.05) 0%, transparent 70%)',
          bottom: '0%',
          right: '10%'
        }} />

        <div style={{ position: 'relative', zIndex: 3, maxWidth: '720px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '9px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '980px',
            padding: '7px 18px',
            fontSize: '12px',
            fontWeight: '500',
            color: 'rgba(255,255,255,0.55)',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              background: '#E8A800',
              borderRadius: '50%',
              boxShadow: '0 0 10px #E8A800'
            }} />
            Insight & Knowledge
          </div>
          <h1 style={{
            fontSize: 'clamp(36px, 5vw, 64px)',
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: '-2px',
            color: '#FFFFFF',
            marginBottom: '20px'
          }}>
            Blog <span style={{ color: '#E8A800' }}>&</span> Artikel
          </h1>
          <p style={{
            fontSize: '18px',
            fontWeight: '300',
            color: 'rgba(255,255,255,0.55)',
            maxWidth: '540px',
            margin: '0 auto',
            lineHeight: 1.8,
            letterSpacing: '-0.2px'
          }}>
            Temukan insight terbaru tentang digital marketing, customer experience, dan strategi bisnis dari tim Aptana.
          </p>
        </div>
      </section>

      {/* Articles Section */}
      <div className="blog-pad" style={{
        borderTop: '1px solid rgba(255,255,255,0.07)',
        maxWidth: '1100px',
        margin: '0 auto'
      }}>
        <div className="blog-inner">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '9px',
              fontSize: '11px',
              fontWeight: '700',
              color: '#E8A800',
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              marginBottom: '18px'
            }}>
              <span style={{
                width: '20px',
                height: '1.5px',
                background: '#E8A800',
                flexShrink: 0
              }} />
              Artikel Terbaru
            </div>
            <h2 style={{
              fontSize: 'clamp(32px, 4vw, 48px)',
              fontWeight: 800,
              lineHeight: 1.1,
              color: '#FFFFFF',
              marginBottom: '16px',
              letterSpacing: '-1.5px'
            }}>
              Update Pengetahuan Bisnis Anda
            </h2>
            <p style={{
              fontSize: '16px',
              fontWeight: '300',
              color: 'rgba(255,255,255,0.55)',
              maxWidth: '460px',
              margin: '0 auto',
              lineHeight: 1.8
            }}>
              Strategi praktis dan tren terkini untuk mengembangkan bisnis di era digital
            </p>
          </div>

          {articles.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '80px 40px',
              background: '#0E1C30',
              borderRadius: '20px',
              border: '1px dashed rgba(255,255,255,0.07)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>📝</div>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#FFFFFF', marginBottom: '8px' }}>Belum Ada Artikel</h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '14px' }}>Artikel akan muncul di sini setelah dipublikasikan.</p>
            </div>
          ) : (
            <div className="blog-grid">
              {articles.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div style={{
        background: '#0B1628',
        borderTop: '1px solid rgba(255,255,255,0.07)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: '100px 0'
      }}>
        <div style={{
          position: 'absolute',
          top: '-100px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '400px',
          background: 'radial-gradient(ellipse, rgba(232,168,0,0.06) 0%, transparent 65%)',
          pointerEvents: 'none'
        }} />
        <div className="blog-inner" style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{
            fontSize: 'clamp(28px, 4vw, 48px)',
            fontWeight: 800,
            lineHeight: 1.05,
            color: '#FFFFFF',
            marginBottom: '18px',
            letterSpacing: '-1.5px'
          }}>
            Butuh Solusi Marketing<br />untuk Bisnis Anda?
          </h2>
          <p style={{
            fontSize: '16px',
            fontWeight: '300',
            color: 'rgba(255,255,255,0.55)',
            maxWidth: '420px',
            margin: '0 auto 32px',
            lineHeight: 1.85
          }}>
            Konsultasi gratis dengan tim Aptana. Kami bantu tentukan strategi terbaik.
          </p>
          <a href="/kontak" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '9px',
            background: '#E8A800',
            color: '#06101E',
            fontWeight: '700',
            fontSize: '15px',
            letterSpacing: '-0.3px',
            padding: '15px 30px',
            borderRadius: '980px',
            textDecoration: 'none',
            transition: 'transform 0.2s, box-shadow 0.2s, background 0.2s'
          }}>
            Konsultasi Gratis
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}

const BLOG_PLACEHOLDER = 'https://supa-site.aptana.co.id/storage/v1/object/public/Aptana-Website/blog/Placeholder%20Artikel.svg';

function ArticleCard({ article }) {
  const [hovered, setHovered] = useState(false);

  // Priority: featured_image field → first <img> in content → placeholder
  const imgMatch = article.content?.match(/<img[^>]+src="([^"]+)"/);
  const featuredImage = article.featured_image || (imgMatch ? imgMatch[1] : null) || BLOG_PLACEHOLDER;

  return (
    <article
      style={{
        background: '#0E1C30',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '20px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.35s, transform 0.35s, box-shadow 0.35s',
        position: 'relative',
        transform: hovered ? 'translateY(-4px) scale(1.01)' : 'translateY(0)',
        boxShadow: hovered ? '0 20px 60px rgba(0,0,0,0.25)' : 'none'
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top border gradient */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)'
      }} />

      {/* Article Image */}
      <div style={{
        position: 'relative',
        height: '200px',
        overflow: 'hidden',
        background: '#112035'
      }}>
        <img
          src={featuredImage}
          alt={article.title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.6s',
            transform: hovered ? 'scale(1.05)' : 'scale(1)'
          }}
          onError={(e) => { e.target.src = BLOG_PLACEHOLDER; }}
        />
      </div>

      {/* Article Content */}
      <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Category pill */}
        <div style={{ marginBottom: '14px' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            padding: '5px 12px',
            borderRadius: '980px',
            background: 'rgba(232,168,0,0.1)',
            color: '#E8A800',
            border: '1px solid rgba(232,168,0,0.2)'
          }}>
            {article.category || 'Insight'}
          </span>
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: 'clamp(16px, 2vw, 19px)',
          fontWeight: '700',
          color: '#FFFFFF',
          lineHeight: 1.45,
          marginBottom: '10px',
          letterSpacing: '-0.4px',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {article.title}
        </h2>

        {/* Date */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          fontSize: '12px',
          color: 'rgba(255,255,255,0.32)',
          fontWeight: '400',
          marginBottom: '14px',
          letterSpacing: '0.1px'
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, flexShrink: 0 }}>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {new Date(article.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
        </div>

        {/* Excerpt */}
        <p style={{
          fontSize: '13px',
          fontWeight: '300',
          color: 'rgba(255,255,255,0.48)',
          lineHeight: 1.75,
          marginBottom: '20px',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          flex: 1
        }}>
          {article.meta_description || article.content?.replace(/<[^>]*>/g, '').substring(0, 120) + '…'}
        </p>

        {/* CTA */}
        <a
          href={`/blog/${article.slug}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            fontWeight: '600',
            color: '#E8A800',
            letterSpacing: '0px',
            textDecoration: 'none',
            transition: 'gap 0.3s, color 0.2s',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            paddingTop: '16px',
            marginTop: 'auto'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.gap = '12px';
            e.currentTarget.style.color = '#FFD166';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.gap = '6px';
            e.currentTarget.style.color = '#E8A800';
          }}
        >
          Baca Selengkapnya
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    </article>
  );
}

// ── Article Detail ────────────────────────────────────────────────────────────

/** Convert basic Markdown bold/italic to HTML for content stored as markdown */
function mdToHtml(markdown) {
  if (!markdown) return '';
  return markdown
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
  // Note: we intentionally do NOT convert \n → <br> here because the
  // content may already contain HTML tags written by the rich-text editor.
}

function ArticleDetailPage({ article, related = [] }) {
  return (
    <div style={{ backgroundColor: '#06101E', color: '#FFFFFF', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
      <style>{articleStyles}</style>
      <SiteHeader currentPage="blog" />

      {/* Hero Section - Dark Theme */}
      <section style={{
        position: 'relative',
        minHeight: '40svh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 'clamp(90px,12vw,120px) clamp(20px,5vw,48px) clamp(48px,8vw,80px)',
        overflow: 'hidden',
        background: '#06101E'
      }}>
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(135deg, rgba(232,168,0,0.03) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />

        <div style={{ position: 'relative', zIndex: 3, maxWidth: '800px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: '13px',
            color: 'rgba(255,255,255,0.55)',
            marginBottom: 24
          }}>
            <a href="/" style={{ color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>Home</a>
            <span>›</span>
            <a href="/blog" style={{ color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>Blog</a>
            <span>›</span>
            <span>{article.title}</span>
          </div>

          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '11px',
            fontWeight: '700',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            padding: '6px 14px',
            borderRadius: '980px',
            background: 'rgba(232,168,0,0.1)',
            color: '#E8A800',
            border: '1px solid rgba(232,168,0,0.2)',
            marginBottom: 20
          }}>
            {article.category || 'Insight'}
          </div>

          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '-1.5px',
            color: '#FFFFFF',
            marginBottom: 20
          }}>
            {article.title}
          </h1>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 20,
            fontSize: '14px',
            color: 'rgba(255,255,255,0.55)'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>📅 {new Date(article.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>📝 {article.author || 'Aptana Team'}</span>
          </div>
        </div>
      </section>

      {/* Article Content Section */}
      <section style={{ background: '#080F1E', padding: 'clamp(40px,8vw,80px) 0' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 clamp(16px,5vw,56px)' }}>
          <div className="detail-card" style={{
            background: '#0E1C30',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: '20px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)'
          }}>
            <img
              src={article.featured_image || BLOG_PLACEHOLDER}
              alt={article.title}
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '12px',
                marginBottom: 32,
                border: '1px solid rgba(255,255,255,0.07)',
                display: 'block'
              }}
              onError={(e) => { e.target.src = BLOG_PLACEHOLDER; }}
            />
            <div
              className="article-content"
              style={{ fontSize: '16px', lineHeight: 1.85 }}
              dangerouslySetInnerHTML={{ __html: mdToHtml(article.content) }}
            />
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {related.length > 0 && (
        <section style={{ background: '#06101E', padding: '80px 0', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="blog-related-inner" style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 clamp(16px,5vw,56px)' }}>
            <div style={{ marginBottom: '48px' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '9px',
                fontSize: '11px', fontWeight: '700', color: '#E8A800',
                letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '14px'
              }}>
                <span style={{ width: '20px', height: '1.5px', background: '#E8A800', flexShrink: 0 }} />
                Baca Juga
              </div>
              <h2 style={{
                fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800,
                color: '#FFFFFF', letterSpacing: '-1px', lineHeight: 1.15
              }}>
                Artikel Lainnya
              </h2>
            </div>
            <div className="blog-grid" style={{ gridTemplateColumns: `repeat(${Math.min(related.length, 3)}, 1fr)` }}>
              {related.map(a => <ArticleCard key={a.id} article={a} />)}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <div style={{
        background: '#0B1628',
        borderTop: '1px solid rgba(255,255,255,0.07)',
        textAlign: 'center',
        padding: '80px 0'
      }}>
        <div className="blog-inner">
          <h2 style={{
            fontSize: 'clamp(26px,4vw,36px)',
            fontWeight: 800,
            color: '#FFFFFF',
            marginBottom: 16,
            letterSpacing: '-1px'
          }}>
            Butuh Solusi Marketing<br />untuk Bisnis Anda?
          </h2>
          <p style={{
            fontSize: '16px',
            color: 'rgba(255,255,255,0.55)',
            maxWidth: '420px',
            margin: '0 auto 32px',
            lineHeight: 1.6
          }}>
            Konsultasi gratis dengan tim Aptana. Kami bantu tentukan strategi terbaik.
          </p>
          <a href="/kontak" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '9px',
            background: '#E8A800',
            color: '#06101E',
            fontWeight: '700',
            fontSize: '15px',
            padding: '15px 30px',
            borderRadius: '980px',
            textDecoration: 'none',
            transition: 'transform 0.2s, box-shadow 0.2s, background 0.2s'
          }}>
            Konsultasi Gratis
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}

// ── Strip dangerous auto-redirects from stored HTML ──────────────────────────
// Only strips unconditional location redirects that execute at page load.
// Preserves onclick="...", script block code, and href attributes intact.
function cleanHTML(html) {
  if (!html) return '';
  // Only strip <meta http-equiv="refresh"> — the only true auto-redirect.
  // Do NOT touch window.location patterns: they may be inside <script> blocks
  // or onclick attributes where they are legitimate navigation code.
  return html.replace(/<meta[^>]*http-equiv=['"]refresh['"][^>]*>/gi, '');
}

// ── CMS Page Renderer ─────────────────────────────────────────────────────────
// dangerouslySetInnerHTML injects HTML but React never executes <script> tags.
// We re-execute every inline script manually after the DOM is populated so that
// IntersectionObserver, counters, navbar scroll, etc. all work correctly.
function CMSPage({ html }) {
  const ref = useRef(null);
  const cleaned = cleanHTML(html);
  // Sanitize against XSS while preserving full-page HTML structure and
  // admin-authored event handlers (onclick for product routing, etc.)
  const purified = DOMPurify.sanitize(cleaned, {
    WHOLE_DOCUMENT: true,
    FORCE_BODY: false,
    ADD_TAGS: ['style', 'meta', 'link', 'title'],
    ADD_ATTR: [
      'name', 'content', 'http-equiv', 'charset', 'rel', 'type',
      'onclick', 'onmouseover', 'onmouseout', 'onmouseenter', 'onmouseleave',
      'target', 'loading', 'decoding', 'srcset', 'sizes',
      'aria-label', 'aria-hidden', 'aria-expanded', 'aria-controls',
      'role', 'tabindex', 'placeholder', 'required', 'autocomplete',
      'data-cfemail', 'action', 'method', 'enctype',
    ],
  });

  useEffect(() => {
    // Use DOMParser (full document parser) to extract script content —
    // avoids innerHTML fragment-parser quirks where <script> raw text
    // can bleed HTML characters and cause "Unexpected token '<'" errors.
    const doc = new DOMParser().parseFromString(cleaned, 'text/html');
    const scripts = Array.from(doc.querySelectorAll('script'));
    const runKeys = [];

    scripts.forEach(old => {
      const type = (old.type || '').toLowerCase();
      if (type && type !== 'text/javascript' && type !== 'module') return;
      const s = document.createElement('script');
      if (old.src) {
        s.src = old.src;
        s.async = old.async;
        document.head.appendChild(s);
      } else {
        const code = old.textContent.trim();
        if (!code) return;
        // Run-once guard: prevents React Strict Mode double-invoke while
        // keeping function declarations at global scope so onclick="fn()"
        // handlers (e.g. toggleMenu, hamburger) work correctly.
        // The cleanup below deletes the flag on unmount so re-navigation re-runs.
        const key = '_cmsScript_' + (code.length * 31 + code.charCodeAt(0)).toString(36);
        runKeys.push(key);
        try {
          s.textContent = `if(!window[${JSON.stringify(key)}]){window[${JSON.stringify(key)}]=1;\n${code}\n}`;
          document.head.appendChild(s);
          document.head.removeChild(s);
        } catch (err) {
          console.warn('[CMSPage] Script execution failed:', err);
        }
      }
    });

    // Remove run-once flags on unmount so scripts re-execute when user
    // navigates back to this page.
    return () => { runKeys.forEach(k => { delete window[k]; }); };
  }, [cleaned]);

  // Browsers don't autoplay videos inserted via innerHTML the same way they
  // do on a normal page load. Explicitly call load()+play() after the DOM
  // is populated so background videos start immediately.
  useEffect(() => {
    if (!ref.current) return;
    const videos = ref.current.querySelectorAll('video[autoplay]');
    videos.forEach(v => {
      v.muted = true;
      v.load();
      v.play().catch(() => { });
    });
  }, [purified]);

  return <div ref={ref} dangerouslySetInnerHTML={{ __html: purified }} />;
}

// ── Newsletter Popup ──────────────────────────────────────────────────────────
function NewsletterPopup({ article, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', animation: 'fadeIn 0.25s ease',
      }}
    >
      <style>{`@keyframes fadeIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}`}</style>
      <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '520px', width: '100%' }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: -14, right: -14, zIndex: 1,
            width: 32, height: 32, borderRadius: '50%',
            background: '#fff', border: 'none', cursor: 'pointer',
            fontSize: 20, lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.3)', fontFamily: 'inherit',
          }}
          aria-label="Tutup"
        >×</button>
        {/* Popup image — click routes to blog slug */}
        <img
          src={article.featured_image}
          alt={article.title}
          onClick={() => { window.location.href = `/blog/${article.slug}`; }}
          style={{
            width: '100%', display: 'block', borderRadius: 16, cursor: 'pointer',
            boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.015)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        />
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
// Route-aware fetching: only loads data needed for the current route.
// - CMS page  → fetches that one page's HTML + popup articles (metadata only)
// - Blog list → fetches article metadata only (no content field)
// - Blog detail → fetches single article + small related articles sample
// This avoids loading megabytes of page HTML and article content on every route.
function WebsitePreview({ currentPage: initialPage = 'home' }) {
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [popup, setPopup] = useState(null);

  useEffect(() => { setCurrentPage(initialPage); }, [initialPage]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setRouteData(null);

    const fetchRoute = async () => {
      try {
        if (currentPage === 'submission-form') {
          if (!cancelled) setRouteData({ type: 'submission-form' });
        } else if (currentPage.startsWith('blog/')) {
          // Article detail: fetch full article + related metadata (no content)
          const slug = currentPage.replace('blog/', '');
          const [artRes, relRes] = await Promise.all([
            supabase.from('articles')
              .select('*')
              .eq('slug', slug)
              .eq('status', 'published')
              .maybeSingle(),
            supabase.from('articles')
              .select('id,title,slug,category,meta_description,featured_image,created_at')
              .eq('status', 'published')
              .neq('slug', slug)
              .order('created_at', { ascending: false })
              .limit(9),
          ]);
          const related = (relRes.data || []).sort(() => Math.random() - 0.5).slice(0, 3);
          if (!cancelled) setRouteData({ type: 'article', article: artRes.data, related });

        } else if (currentPage === 'blog' || currentPage === 'artikel') {
          // Blog listing: metadata only — no content field (huge savings)
          const artRes = await supabase.from('articles')
            .select('id,title,slug,category,meta_description,featured_image,created_at,is_popup,popup_pages')
            .eq('status', 'published')
            .order('created_at', { ascending: false });
          if (!cancelled) setRouteData({ type: 'blog', articles: artRes.data || [] });

        } else {
          // CMS page: fetch only this page's HTML + popup candidates
          const slug = currentPage === 'home' ? 'home' : currentPage;
          const [pageRes, popupRes] = await Promise.all([
            supabase.from('pages')
              .select('id,slug,name,html')
              .eq('status', 'published')
              .eq('slug', slug)
              .maybeSingle(),
            supabase.from('articles')
              .select('id,title,slug,featured_image,is_popup,popup_pages')
              .eq('status', 'published')
              .eq('is_popup', true),
          ]);
          if (!cancelled) setRouteData({ type: 'cms', page: pageRes.data, popupArticles: popupRes.data || [] });
        }
      } catch (err) {
        console.error('WebsitePreview fetch error:', err);
        if (!cancelled) setRouteData({ type: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRoute();
    return () => { cancelled = true; };
  }, [currentPage]);

  // Popup logic for CMS pages
  useEffect(() => {
    if (!routeData || routeData.type !== 'cms') return;
    const slug = currentPage === 'home' ? 'home' : currentPage;
    const candidate = (routeData.popupArticles || []).find(a =>
      Array.isArray(a.popup_pages) &&
      a.popup_pages.includes(slug) &&
      a.featured_image &&
      !sessionStorage.getItem(`popup_dismissed_${a.id}`)
    );
    setPopup(candidate || null);
  }, [routeData, currentPage]);

  // Popup logic for blog listing
  useEffect(() => {
    if (!routeData || routeData.type !== 'blog') return;
    const candidate = (routeData.articles || []).find(a =>
      a.is_popup &&
      Array.isArray(a.popup_pages) &&
      a.popup_pages.includes('blog') &&
      a.featured_image &&
      !sessionStorage.getItem(`popup_dismissed_${a.id}`)
    );
    setPopup(candidate || null);
  }, [routeData]);

  const closePopup = () => {
    if (popup) sessionStorage.setItem(`popup_dismissed_${popup.id}`, '1');
    setPopup(null);
  };

  if (loading) return <LoadingScreen />;
  if (!routeData || routeData.type === 'error') return <NotFoundPage slug={currentPage} />;

  if (routeData.type === 'submission-form') return <SubmissionFormPage />;

  if (routeData.type === 'article') {
    if (!routeData.article) return <NotFoundPage slug={currentPage} />;
    return <ArticleDetailPage article={routeData.article} related={routeData.related} />;
  }

  if (routeData.type === 'blog') {
    return (
      <>
        <BlogListingPage articles={routeData.articles} />
        {popup && <NewsletterPopup article={popup} onClose={closePopup} />}
      </>
    );
  }

  // CMS page
  if (!routeData.page) return <NotFoundPage slug={currentPage} />;
  return (
    <>
      <CMSPage html={routeData.page.html} />
      {popup && <NewsletterPopup article={popup} onClose={closePopup} />}
    </>
  );
}

export default WebsitePreview;
